import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Repeat, Clock, Pause, Play, Trash2, Loader2, AlertTriangle,
} from 'lucide-react';
import {
  aiAssistantService,
  type AiSchedule,
} from '@/services/aiAssistantService';

/**
 * Compact row above the chat showing every schedule attached to the open
 * conversation. The AI creates these via the create_loop / create_schedule
 * tools; the user can disable/re-enable/delete here without leaving the chat.
 *
 * Renders nothing when the conversation has no schedules (keeps the chat
 * uncluttered for normal conversations).
 */
interface Props {
  conversationId: string;
  /** Bump when the user sends a message that may have created a schedule —
   *  causes the row to refetch. */
  refreshKey?: number;
}

export const ConversationSchedulesRow: React.FC<Props> = ({ conversationId, refreshKey }) => {
  const [rows, setRows] = useState<AiSchedule[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Monotonic request id — overlapping loads (the useEffect refresh path
  // + a post-action reload firing close together) must not let an older
  // fetch resolve last and clobber the newer state with stale rows.
  const reqRef = useRef(0);

  /** Bare fetch + apply — RETHROWS on failure so callers in act() can
   *  detect a reload failure and trigger the optimistic fallback. The
   *  useEffect-driven load() below wraps this with a swallow-and-set-error
   *  policy because there's nowhere to surface the throw to in that path. */
  const fetchAndApply = useCallback(async () => {
    const myReq = ++reqRef.current;
    const fetched = await aiAssistantService.listConversationSchedules(conversationId);
    if (myReq !== reqRef.current) return;   // a newer fetch is in flight
    setRows(fetched);
    setError(null);
  }, [conversationId]);

  const load = useCallback(async () => {
    try { await fetchAndApply(); }
    catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load schedules');
    }
  }, [fetchAndApply]);

  useEffect(() => { void load(); }, [load, refreshKey]);

  type ActionKind = 'enable' | 'disable' | 'delete';
  /** Apply the action's expected effect locally — used as a fallback when
   *  the reconciling reload after a successful action fails. Otherwise the
   *  panel would keep showing pre-action state (including just-deleted
   *  rows) and let users click again against a backend that's already
   *  moved on. */
  const applyOptimistic = (id: string, kind: ActionKind) => {
    setRows((prev) => {
      if (kind === 'delete') return prev.filter((s) => s.id !== id);
      return prev.map((s) => s.id === id
        ? { ...s, enabled: kind === 'enable' }
        : s);
    });
  };

  const act = async (id: string, kind: ActionKind, fn: () => Promise<unknown>) => {
    if (busyId) return;
    setBusyId(id);
    setError(null);
    try {
      await fn();
      // Reconcile against server truth via the throwing variant — load()
      // swallows errors for the useEffect path, so calling it here would
      // never trigger the optimistic fallback. fetchAndApply rethrows so
      // we can detect a reload failure and patch state locally.
      try {
        await fetchAndApply();
      } catch (reloadErr) {
        applyOptimistic(id, kind);
        throw reloadErr;
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setBusyId(null);
    }
  };

  if (rows.length === 0 && !error) return null;

  return (
    <div className="rounded-md border bg-muted/30 px-3 py-2 space-y-1">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">
        Schedules on this conversation
      </div>
      {error && (
        <div className="text-xs text-destructive flex items-center gap-1.5">
          <AlertTriangle className="h-3 w-3" /> {error}
        </div>
      )}
      <ul className="space-y-1">
        {rows.map((s) => (
          <li
            key={s.id}
            className={`flex items-center gap-2 rounded bg-card px-2 py-1.5 text-xs ${
              s.enabled ? '' : 'opacity-60'
            }`}>
            {s.kind === 'Recurring' ? (
              <Repeat className="h-3.5 w-3.5 text-primary" />
            ) : (
              <Clock className="h-3.5 w-3.5 text-primary" />
            )}
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{s.name}</div>
              <div className="text-[11px] text-muted-foreground truncate">
                {s.kind === 'Recurring'
                  ? `every ${s.intervalMinutes}m`
                  : s.runAtUtc
                    ? `at ${new Date(s.runAtUtc).toLocaleString()}`
                    : 'one-shot'}
                {' · '}
                {s.enabled
                  ? s.nextRunAtUtc
                    ? `next ${formatNext(s.nextRunAtUtc)}`
                    : 'enabled'
                  : 'disabled'}
                {s.runCount > 0 && ` · ran ${s.runCount}×`}
                {s.errorCount > 0 && (
                  <span className="text-destructive"> · {s.errorCount} err</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              {s.enabled ? (
                <Button
                  variant="ghost" size="sm" className="h-6 w-6 p-0"
                  title="Disable"
                  disabled={busyId === s.id}
                  onClick={() => void act(s.id, 'disable', () => aiAssistantService.disableSchedule(s.id))}>
                  {busyId === s.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Pause className="h-3 w-3" />}
                </Button>
              ) : (
                <Button
                  variant="ghost" size="sm" className="h-6 w-6 p-0"
                  title="Re-enable"
                  disabled={busyId === s.id}
                  onClick={() => void act(s.id, 'enable', () => aiAssistantService.enableSchedule(s.id))}>
                  {busyId === s.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
                </Button>
              )}
              <Button
                variant="ghost" size="sm" className="h-6 w-6 p-0 hover:text-destructive"
                title="Delete schedule"
                disabled={busyId === s.id}
                onClick={() => {
                  if (window.confirm(`Delete schedule "${s.name}"? Past run history is kept.`))
                    void act(s.id, 'delete', () => aiAssistantService.deleteSchedule(s.id));
                }}>
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

function formatNext(iso: string): string {
  const diffMs = new Date(iso).getTime() - Date.now();
  if (diffMs < 0) return 'pending';
  const m = Math.round(diffMs / 60_000);
  if (m < 1) return 'now';
  if (m < 60) return `in ${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `in ${h}h`;
  return new Date(iso).toLocaleString();
}

export default ConversationSchedulesRow;
